import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Route, Router } from "@angular/router";
import { BehaviorSubject, filter, map, merge } from "rxjs";
import { RequestApprovalTypeDetailsStore } from "../store/approval-type-details-store";
import { ApprovalTypesService } from "../approval-type/approval-type";
import { TABS } from "../../enum/request/TABS";
import { MenuItem } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import { requestRoutes } from '../../app.routes';


@Injectable({
    providedIn: 'root'
})
export class ProgressService {

    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private requestApprovalTypeDetailsStore = inject(RequestApprovalTypeDetailsStore);
    private approvalTypesService = inject(ApprovalTypesService);
    private translateService = inject(TranslateService);

    tabsText: any;

    private activeTabsSubject = new BehaviorSubject<MenuItem[]>([]);
    activeTabs$ = this.activeTabsSubject.asObservable();

    private currentStepSubject = new BehaviorSubject<number>(1);
    currentStep$ = this.currentStepSubject.asObservable();

    constructor() {
        this.initText();
    }


    private progressSubscribed = false;

    initText() {
        this.translateService.stream('tabs').subscribe(translation => {
            this.tabsText = translation;
            if (!this.progressSubscribed) {
                this.progressSubscribed = true;
                this.subscribeToProgressUpdates();
            }
        });
    }
    private subscribeToProgressUpdates(): void {

        merge(
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                map(event => ({ value: event, source: 'router' }))
            ),
            this.requestApprovalTypeDetailsStore.getApprovalTabs().pipe(
                map(value => ({ value, source: 'approvalTabs' })))
        )
            .subscribe((data) => {
                this.updateActiveTabs(data);
                this.updateCurrentStep();
            });

    }

    private updateActiveTabs(data: any) {
        const isInProductTab = data.source === 'router' && this.router.url.includes('/product/');
        const isTabListUpdated = data.source === 'approvalTabs' && data.value.length;

        if (isInProductTab) {
            this.updateActiveTabsByProduct()
        }
        else if (isTabListUpdated) {
            this.updateActiveTabsByRoute();
        }

    }

    private updateActiveTabsByRoute(): void {
        setTimeout(() => {
            const routConfig = requestRoutes;
            const currentTabs = this.activeTabsSubject.getValue();
            const isNeedTabToProgress = (route: Route) => {
                return route.data &&
                    route.data['stepCondition'] &&
                    (route.data['stepCondition'] === true ||
                        (route.data['stepCondition'] === 'isExistTab' &&
                            this.approvalTypesService.isExistTabToApproval(route.data['step'])));
            };

            const routesToProgress = routConfig.filter(route => isNeedTabToProgress(route));

            let menuTabToProgress: MenuItem[] = routesToProgress.map(route => ({
                step: route.data!['step'],
                path: route['path']!,
                label: this.tabsText[route.data!['title']?.toString()],
                styleClass: ''
            }));


            const existingDynamicProducts = currentTabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);

            if (existingDynamicProducts.length > 0) {
                menuTabToProgress = menuTabToProgress.filter(tab => tab['step'] !== TABS.PRODUCTS);
                menuTabToProgress.push(...existingDynamicProducts);
            }

            menuTabToProgress.sort((a, b) => (a['step'] || 0) - (b['step'] || 0));

            if (menuTabToProgress.length > 0) {
                menuTabToProgress.forEach(t => t.styleClass = '');
                menuTabToProgress[menuTabToProgress.length - 1].styleClass = 'mi-last-item';
            }

            this.activeTabsSubject.next(menuTabToProgress);
        });
    }

    private updateActiveTabsByProduct(): void {
        const tabs = this.activeTabsSubject.getValue();

        const activeGuid = this.getDeepestRouteParam('guid') as string;

        const activeTab = tabs.find(tab => tab['guid'] === activeGuid);

        const productsTab = tabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);
        if (!activeTab) {
            tabs.push({
                step: TABS.PRODUCTS,
                path: 'product', guid: activeGuid,
                label: this.tabsText['products'] + ' ' + (productsTab.length + 1)
            })
        }
        const updatedTabs = tabs.filter(tab => tab['step'] !== TABS.PRODUCTS || !!tab['guid']);
        updatedTabs.sort((a, b) => a['step'] - b['step'])

        this.activeTabsSubject.next(updatedTabs);

    }

    private getDeepestRouteParam(paramName: string): string | null {
        let currentRoute = this.activatedRoute.root;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }
        return currentRoute.snapshot.paramMap.get(paramName);
    }

    private updateCurrentStep(): void {
        const currentUrl = this.router.url;
        const tabs = this.activeTabsSubject.getValue();

        const urlParts = currentUrl.split('/').filter(part => part && part !== 'request' && part !== 'new-request');
        let relativePath = urlParts.length > 0 ? urlParts[0] : '';

        if (urlParts.includes('product') && urlParts.length > 1) {
            relativePath = urlParts[0];
        } else if (urlParts.length > 1 && (currentUrl.includes('request') && !currentUrl.includes('new-request'))) {
            relativePath = urlParts[1];
        } else if (currentUrl.includes('new-request')) {
            relativePath = urlParts[0];
        }


        if (relativePath.includes('?')) {
            relativePath = relativePath.split('?')[0];
        }

        if (!relativePath) return;

        const isCurrentTab = (tab: MenuItem) => {
            if (tab['step'] !== TABS.PRODUCTS) {
                return tab['path'] === relativePath;
            }
            else if (tab['guid']) {
                return tab['path'] === 'product' && currentUrl.includes(tab['guid']);
            }
            return false;
        }

        const currentTab = tabs.find(tab => isCurrentTab(tab));

        if (currentTab) {
            const index = tabs.indexOf(currentTab) + 1;
            this.currentStepSubject.next(index);
        }
    }

    removeProductTab(guid: string) {
        const tabs = this.activeTabsSubject.getValue();
        const updatedTabs = tabs.filter(tab => tab['guid'] !== guid);

        const productTabs = updatedTabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);
        productTabs.forEach((tab, index) => {
            tab.label = this.tabsText['products'] + ' ' + (index + 1);
        });

        this.activeTabsSubject.next(updatedTabs);
    }

    /**
     * Initializes product tabs in the progress bar for an existing request.
     * Called after products are loaded from the server.
     */
    initProductTabs(productGuids: string[]) {
        const tabs = this.activeTabsSubject.getValue();

        // Remove any existing generic product tab placeholder
        const updatedTabs = tabs.filter(tab => tab['step'] !== TABS.PRODUCTS || !!tab['guid']);

        // Add a tab for each product
        productGuids.forEach((guid, index) => {
            if (!updatedTabs.find(tab => tab['guid'] === guid)) {
                updatedTabs.push({
                    step: TABS.PRODUCTS,
                    path: 'product',
                    guid: guid,
                    label: this.tabsText['products'] + ' ' + (index + 1),
                    styleClass: ''
                });
            }
        });

        updatedTabs.sort((a, b) => (a['step'] || 0) - (b['step'] || 0));

        if (updatedTabs.length > 0) {
            updatedTabs.forEach(t => t.styleClass = '');
            updatedTabs[updatedTabs.length - 1].styleClass = 'mi-last-item';
        }

        this.activeTabsSubject.next(updatedTabs);
    }

    resetTabs() {
        this.activeTabsSubject.next([]);
        this.currentStepSubject.next(1);
    }
}